
import { IInputType } from "@interfaces/IInputType";
import { IPlot } from "@interfaces/IPlot";
import { HardwareSource, InputStateDescription, IVehicleInput } from "@interfaces/IVehicleInput";
import { InputTypeService } from "@services/inputType.service";
import { ReeferInputType } from "@temperature/_enums/reefer/ReeferInputType.enum";
import * as angular from "angular";
import { CacheService } from "./cache.Service";

export interface IVehicleInputRecordHistory {

    Id: number;
    VehicleId: number;
    InputId: number;
    StartUtc: Date;
    EndUtc: Date;
    StartPlotId: number;
    EndPlotId: number;
    TriggerInputState: InputState;

    // ---

    inputName: string;

    inputStateDescription: InputStateDescription;

    startAddress: string;
    endAddress: string;

    startPlot?: IPlot;
    endPlot?: IPlot;
}

export interface IVehicleInputRecord {
    Id: number;
    PlotId: number;
    VehicleId?: number;
    InputId?: number;
    ActiveState: InputState;
    Utc: Date;
    Position: number;
    Source: HardwareSource;
    ZoneId?: number;
    Type?: ReeferInputType;
    ZonePosition?: number;
}

export enum InputState {
    Low = 0,
    High = 1
}

export class VehicleInputService {

    private vehicleInputsCache = "vehicleInputsCache";

    static $inject = ["$http", "inputTypeService", "cacheService"];

    constructor(
        private $http: angular.IHttpService,
        private inputTypeService: InputTypeService,
        private readonly cacheService: CacheService
    ) {

        this.cacheService.createCache(this.vehicleInputsCache, { maxAge: 60 * 60 * 1000 });
    }

    getVehicleInputs(vehicleId: number = null, invalidateCache: boolean = false): angular.IPromise<IVehicleInput[]> {
        return this.getOrRetrieveVehicleInputs(vehicleId, invalidateCache)
            .then((inputs: IVehicleInput[]) => {
                inputs.forEach(input => {
                    input.inputSourceString = HardwareSource[input.Source];

                });
                return this.inputTypeService.getInputTypes().then(types => {
                    this.mapInputTypesToInputs(inputs, types);

                    if (vehicleId) {
                        return inputs.filter(input => input.VehicleId == vehicleId);
                    }

                    return inputs;
                });
            });
    }

    getVehicleInputsFromDatabase(vehicleId: number): angular.IPromise<IVehicleInput[]> {
        return this
            .$http({
                url: "/api/inputs",
                method: "GET",
            })
            .then((response: angular.IHttpResponse<IVehicleInput[]>) => {

                return response.data;
            });
    }

    getVehicleInputHistory(vehicleId: number, start: Date, end: Date, includeOverlapping: boolean = true, invalidateCache: boolean = false): angular.IPromise<IVehicleInputRecordHistory[]> {
        return this
            .$http({
                url: `/api/inputs/${vehicleId}/history`,
                method: "GET",
                cache: true,
                params: {
                    start: start,
                    end: end,
                    includeOverlapping: includeOverlapping,
                    minsToLive: 5,
                    invalidateCache: invalidateCache
                }
            })
            .then((result: angular.IHttpResponse<IVehicleInputRecordHistory[]>) => {
                return result.data;
            });
    }

    getVehicleInputHistoryById(id: number): angular.IPromise<IVehicleInputRecordHistory> {
        return this
            .$http({
                url: `/api/inputs/history/${id}`,
                method: "GET",
                cache: false
            })
            .then((result: angular.IHttpResponse<IVehicleInputRecordHistory>) => {
                return result.data;
            });
    }

    saveInputs(vehicleId: number, inputs: IVehicleInput[] = []): angular.IPromise<IVehicleInput[]> {
        return this
            .$http({
                url: "/api/inputs/update",
                method: "POST",
                data: inputs
            })
            .then(x => {
                inputs.forEach(input => {
                    this.cacheService.addOrUpdateItem(this.vehicleInputsCache, input, (v1, v2) => v1.Id == v2.Id);
                });

                return this.getVehicleInputs(vehicleId);
            });
    }

    getInputForVehicle(vehicleId: number, inputs: IVehicleInput[]): IVehicleInput {
        for (let i = 0; i < inputs.length; i++) {
            const input = inputs[i];

            if (input.VehicleId === vehicleId) {
                return input;
            }
        }

        return null;
    }

    // ---

    private getOrRetrieveVehicleInputs(vehicleId: number, invalidateCache: boolean = false, unsafe = false): angular.IPromise<IVehicleInput[]> {
        return this.cacheService.getOrRetrieveData(this.vehicleInputsCache, () => this.getVehicleInputsFromDatabase(vehicleId), invalidateCache, unsafe);
    }

    private mapInputTypesToInputs(inputs: IVehicleInput[], inputTypes: IInputType[]) {
        inputs.forEach(input => {

            var matchingInputType = inputTypes.find(type => type.Id === input.InputTypeId);

            if (matchingInputType) {
                input.InputStateDescription = matchingInputType.InputStateDescription
                input.name = matchingInputType.Name
            }

            if (!input.name) {
                input.name = `Unknown Input ${HardwareSource[input.Source]} ${input.Position}`;
            }
        });
    }
}
